import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, ImageIcon, Sparkles, Download, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import GalaxyBackground from "@/components/GalaxyBackground";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Personal AI artwork gallery — shows every dream the user has generated
 * artwork for. Click a card to open the dream detail page.
 */
export default function MyArtworks({ user }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/dreams/artworks/gallery`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setItems(res.data.items || []);
      } catch (e) {
        console.error("Failed to fetch artworks");
      } finally {
        setLoading(false);
      }
    };
    fetchArtworks();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const locale = { fi: "fi-FI", en: "en-US", fr: "fr-FR", de: "de-DE", es: "es-ES" }[i18n.language] || "fi-FI";
    return new Date(dateStr).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
  };

  const handleDownload = (e, item) => {
    e.stopPropagation();
    const mimeType = item.artwork_mime || "image/png";
    const ext = mimeType.includes("jpeg") || mimeType.includes("jpg") ? "jpg" : "png";
    const byteChars = atob(item.artwork);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `unipaivakija-${(item.title || "artwork").replace(/[^a-z0-9]/gi, "-").toLowerCase()}.${ext}`;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen relative">
      <GalaxyBackground />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="text-void/70 hover:text-void mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("common.back") || "Takaisin"}
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-light text-void mb-2 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-500" />
            {t("artworks.title") || "Unitaiteeni"}
          </h1>
          <p className="text-void/60 font-body">
            {t("artworks.subtitle") || "Kaikki AI:n luomat unitaiteesi yhdessä paikassa"}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-void/60 font-body" data-testid="artworks-loading">
            {t("common.loading") || "Ladataan..."}
          </div>
        ) : items.length === 0 ? (
          <div
            className="glass-effect rounded-3xl p-12 text-center"
            data-testid="artworks-empty"
          >
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mx-auto mb-6">
              <ImageIcon className="w-10 h-10 text-purple-500" />
            </div>
            <h2 className="font-heading text-2xl font-light text-void mb-2">
              {t("artworks.emptyTitle") || "Ei vielä unitaiteita"}
            </h2>
            <p className="text-void/60 font-body mb-6 max-w-md mx-auto">
              {t("artworks.emptyDesc") || "Luo ensimmäinen AI-unitaide menemällä jonkin unesi sivulle ja klikkaamalla 'Luo unitaide'."}
            </p>
            <Button
              onClick={() => navigate("/dreams")}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full px-6"
              data-testid="go-to-dreams-btn"
            >
              {t("artworks.goToDreams") || "Selaa unia"}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="artworks-grid">
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(`/dreams/${item.id}`)}
                className="group relative overflow-hidden rounded-3xl bg-void/5 hover:scale-[1.02] transition-transform duration-300 text-left"
                data-testid={`artwork-card-${item.id}`}
              >
                <img
                  src={`data:${item.artwork_mime || "image/png"};base64,${item.artwork}`}
                  alt={item.title}
                  className="w-full aspect-square object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/90 via-void/40 to-transparent opacity-100" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="font-heading text-lg font-medium mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-xs text-white/80 font-body flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    {formatDate(item.date)}
                  </p>
                </div>
                <button
                  onClick={(e) => handleDownload(e, item)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/40 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Lataa"
                  data-testid={`download-artwork-${item.id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
